/* eslint-disable react-hooks/rules-of-hooks */
import {
  FilteredModifierValues,
  FilteredValues,
  GroupPrices,
  Modifier,
  PriceDeviation,
  PriceDeviationResponse,
  StoreProduct,
  Variant,
} from '@/types/menus.types';
import { StoreAndGroupType } from '@/context/context.types';
import {
  isModifierCategoryWithModifierMultiSelect,
  isModifierCategoryWithSubcategoryFilter,
} from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/modifierDisplay.helpers';
import _ from 'lodash';

// Price range constants
const SPECIAL_MIN_PRICE = 0.0;
const SPECIAL_MAX_PRICE = 99.99;
const PRICE_TOLERANCE = 0.001;

const normalizeMenuData = (filteredProducts: StoreProduct[]): StoreProduct[] => {
  const allProducts = filteredProducts;

  return allProducts;
};

const normalizeMenuModifiersData = (filteredProducts: Modifier[]): Modifier[] => {
  const allProducts = filteredProducts;

  return allProducts;
};

export const getDinamicHeaders = (storesAndGroups: StoreAndGroupType) => {
  let allHeaders = storesAndGroups.storeGroups?.flatMap((group) => group.storeGroup) || [];
  if (allHeaders.length === 0) {
    allHeaders = storesAndGroups.stores?.flatMap((group) => group.storeGroup || []) || [];
  }
  const uniq = _.uniqBy(allHeaders, (header) => header);
  const sorted = _.sortBy(uniq, (header) => header);

  return sorted;
};

const filterProducts = (product: StoreProduct, filteredValues: FilteredValues): boolean => {
  const { productName = '', option1 = '', option2 = '', subCategory = '' } = filteredValues;
  const description = product.productDescription.toLowerCase();
  const name = product.productName.toLowerCase();
  const productCode = product.productCode.toLowerCase();

  let optionOk = true;
  if (option1 && option2) {
    optionOk = description.includes(option1.toLowerCase()) && description.includes(option2.toLowerCase());
  } else if (option1) {
    optionOk = description.includes(option1.toLowerCase());
  } else if (option2) {
    optionOk = description.includes(option2.toLowerCase());
  }
  const nameOK = productName ? name.includes(productName.toLowerCase()) : true;
  const productCodeOK = subCategory ? productCode.includes(subCategory.toLowerCase()) : true;

  return optionOk && nameOK && productCodeOK && product.storeGroups.length > 0;
};

const filterModifiers = (product: Modifier, filteredValues: FilteredModifierValues): boolean => {
  const {
    modifierName = '',
    option1 = '',
    option2 = '',
    subCategory = '',
    category = '',
    modifierId = '',
    modifierIds,
  } = filteredValues;
  const description = product.modifierDescription.toLowerCase();
  const name = product.modifierName.toLowerCase();

  if (isModifierCategoryWithSubcategoryFilter(category) && subCategory && product.slotId !== subCategory) {
    return false;
  }

  if (
    isModifierCategoryWithModifierMultiSelect(category) &&
    modifierIds?.length &&
    !modifierIds.includes(product.modifierId)
  ) {
    return false;
  }
  if (
    isModifierCategoryWithModifierMultiSelect(category) &&
    !modifierIds?.length &&
    modifierId &&
    product.modifierId !== modifierId
  ) {
    return false;
  }

  let optionOk = true;
  if (option1 && option2) {
    optionOk = description.includes(option1.toLowerCase()) && description.includes(option2.toLowerCase());
  } else if (option1) {
    optionOk = description.includes(option1.toLowerCase());
  } else {
    optionOk = description.includes(option2.toLowerCase());
  }
  const nameOK = name.includes(modifierName.toLowerCase()) && product.storeGroups.length > 0;

  return optionOk && nameOK;
};

export const getStoreFilteredProducts = (prods: StoreProduct[], filteredValues: FilteredValues): StoreProduct[] => {
  const { price = '' } = filteredValues;
  let filteredProducts: StoreProduct[] = prods;
  if (price) {
    filteredProducts = prods
      .map((product) => ({
        ...product,
        storeGroups: product.storeGroups.filter((storeGroup) => {
          let priceValue: number | null = null;
          if (price.startsWith('$')) {
            priceValue = Number(price.replace('$', ''));
          }
          if (priceValue !== null) {
            const { chosenPriceRange } = storeGroup;

            if (typeof chosenPriceRange === 'number') {
              return chosenPriceRange === priceValue;
            }

            if (typeof chosenPriceRange === 'string') {
              const [min, max] = chosenPriceRange.split('-').map(Number);
              if (max === undefined) {
                const chosenPriceValue = Number(chosenPriceRange);
                return chosenPriceValue === priceValue;
              }
              return priceValue >= min && priceValue <= max;
            }
          }
          return true;
        }),
      }))
      .filter((product) => filterProducts(product, filteredValues));
  } else {
    filteredProducts = prods.filter((product) => filterProducts(product, filteredValues));
  }

  return normalizeMenuData(filteredProducts);
};

export const getStoreFilteredModifiers = (prods: Modifier[], filteredValues: FilteredModifierValues): Modifier[] => {
  const { price = '' } = filteredValues;
  let filteredProducts: Modifier[] = prods;
  if (price) {
    filteredProducts = prods
      .map((product) => ({
        ...product,
        storeGroups: product.storeGroups.filter((storeGroup) => {
          let priceValue: number | null = null;
          if (price.startsWith('$')) {
            priceValue = Number(price.replace('$', ''));
          }
          if (priceValue !== null) {
            const { chosenPriceRange } = storeGroup;

            if (typeof chosenPriceRange === 'number') {
              return chosenPriceRange === priceValue;
            }

            if (typeof chosenPriceRange === 'string') {
              const [min, max] = chosenPriceRange.split('-').map(Number);
              if (max === undefined) {
                const chosenPriceValue = Number(chosenPriceRange);
                return chosenPriceValue === priceValue;
              }
              return priceValue >= min && priceValue <= max;
            }
          }
          return true;
        }),
      }))
      .filter((product) => filterModifiers(product, filteredValues));
  } else {
    filteredProducts = prods.filter((product) => filterModifiers(product, filteredValues));
  }

  return normalizeMenuModifiersData(filteredProducts);
};

export function getStores(storesAndGroups: StoreAndGroupType) {
  if (storesAndGroups.origin === 'tab-store') {
    return storesAndGroups.stores?.map((store) => store.storeNo) || [];
  }
  return storesAndGroups.storeGroups?.flatMap((group) => group.stores) || [];
}

export function getStoreGroups(storesAndGroups: StoreAndGroupType) {
  if (storesAndGroups.origin === 'tab-store') {
    return [];
  }
  return storesAndGroups.storeGroups?.filter((group) => group.stores.length === group.storeCount) || [];
}

export function isPriceInvalid(inputPrice: string, lowestValidPrice: string, highestValidPrice: string) {
  const price = Number(inputPrice.replace('$', ''));
  const lowerPrice = Number(lowestValidPrice);
  const highPrice = Number(highestValidPrice);

  const invalid = price < lowerPrice || price > highPrice;

  return invalid;
}

export function combineStoreGroupPrices(storeProducts: StoreProduct[], storesAndGroups: StoreAndGroupType) {
  const storeGroupList = storeProducts?.flatMap((product) => product.storeGroups);

  const count = storesAndGroups?.countByStoreGroup;

  const result = storeGroupList.reduce((acc: GroupPrices[], curr) => {
    const existingStoreGroup = acc.find((group) => group.storeGroupName === curr?.storeGroupName);

    //values used when FZ user selects stores via StoreGroup tab in menu page
    const totalStoreCount = count?.find((obj) => obj.storeGroup === curr?.storeGroupName);
    const selectStoreGroupCount = storesAndGroups.storeGroups?.filter(
      (group) => group.storeGroup === curr?.storeGroupName,
    );

    //values used when FZ user selects stores via All Stores tab in menu page
    const selectAllStoreCount = storesAndGroups.stores?.filter((group) => group.storeGroup === curr?.storeGroupName);
    const selectedAllStores = selectAllStoreCount?.filter((store) => store.storeGroup === curr?.storeGroupName);
    const selectedAllStoreCount = selectedAllStores?.length;

    if (existingStoreGroup) {
      existingStoreGroup.prices.push(curr?.chosenPriceRange);
    } else if (selectStoreGroupCount) {
      acc.push({
        storeGroupName: curr?.storeGroupName,
        prices: [curr?.chosenPriceRange],
        selectedStoreCount: selectStoreGroupCount[0]?.stores?.length,
        totalStoreCount: selectStoreGroupCount[0]?.storeCount,
        stores: curr.stores,
      });
    } else if (selectAllStoreCount) {
      acc.push({
        storeGroupName: curr?.storeGroupName,
        prices: [curr?.chosenPriceRange],
        selectedStoreCount: selectedAllStoreCount || 0,
        totalStoreCount: totalStoreCount?.count || 0,
        stores: curr.stores,
      });
    }
    return acc;
  }, []);
  return result;
}
export function splitArray(toSplit: any[] = []) {
  const middleIndex = Math.ceil(toSplit.length / 2);
  const firstHalf = toSplit.slice(0, middleIndex);
  const secondHalf = toSplit.slice(middleIndex);
  return [firstHalf, secondHalf];
}

export const checkIfHasVariants = (variants: Variant[]) =>
  variants && variants?.some((variant) => variant.excludedStores && variant.excludedStores.length > 0);

export const getUniqStores = (variants: Variant[]) => {
  const variantStores = variants.flatMap((sp) => sp.variantStores);
  const uniqueStores = _.uniqBy(variantStores, (store) => store);

  return uniqueStores || [];
};

export function getRenderablePriceDeviationsData(data: PriceDeviationResponse[]) {
  const storesSuperSet: Set<string> = new Set();
  data.forEach((product: PriceDeviationResponse) => {
    product.priceDeviations.forEach((deviation: PriceDeviation) => storesSuperSet.add(deviation.storeNumber));
  });
  const sortedStores: string[] = Array.from(storesSuperSet).sort();
  const renderableData = data.map((product: PriceDeviationResponse) => {
    const newPriceDeviations: PriceDeviation[] = [];
    sortedStores.forEach((store: string) => {
      const deviation = product.priceDeviations.find((item: PriceDeviation) => item.storeNumber === store);
      if (deviation) {
        newPriceDeviations.push(deviation);
      } else {
        newPriceDeviations.push({ storeNumber: store, price: '' });
      }
    });
    return { ...product, priceDeviations: newPriceDeviations };
  });
  return { sortedStores, renderableData };
}

export function calculateHighestValidPrice(maxValidPrices: number[]): string {
  if (maxValidPrices.length === 0) {
    return '';
  }

  // Check if any max price contains the special max price
  const hasSpecialMaxPrice = maxValidPrices.some((price) => price === SPECIAL_MAX_PRICE);

  if (hasSpecialMaxPrice) {
    // Filter out special max price and check if there are other max values
    const otherMaxPrices = maxValidPrices.filter((price) => price !== SPECIAL_MAX_PRICE);

    if (otherMaxPrices.length > 0) {
      // Take the highest of the other max prices
      return Math.max(...otherMaxPrices).toFixed(2);
    } else {
      // If no other max prices, use special max price
      return SPECIAL_MAX_PRICE.toFixed(2);
    }
  }

  // If no special max price, use normal logic
  return Math.max(...maxValidPrices).toFixed(2);
}

export function calculateValidPriceRange(
  minValidPrices: number[],
  maxValidPrices: number[],
): { lowestValidPrice: string; highestValidPrice: string } {
  if (minValidPrices.length === 0 && maxValidPrices.length === 0) {
    return { lowestValidPrice: '', highestValidPrice: '' };
  }

  // Remove any undefined/null values and ensure they're numbers
  const cleanMinPrices = minValidPrices.filter((price) => price != null && !isNaN(price));
  const cleanMaxPrices = maxValidPrices.filter((price) => price != null && !isNaN(price));

  if (cleanMinPrices.length === 0 && cleanMaxPrices.length === 0) {
    return { lowestValidPrice: '', highestValidPrice: '' };
  }

  let lowestValidPrice = '';
  let highestValidPrice = '';

  // Check for special values using tolerance for floating point comparison
  const hasSpecialMinPrice = cleanMinPrices.some((price) => Math.abs(price - SPECIAL_MIN_PRICE) < PRICE_TOLERANCE);
  const hasSpecialMaxPrice = cleanMaxPrices.some((price) => Math.abs(price - SPECIAL_MAX_PRICE) < PRICE_TOLERANCE);

  // Handle minimum prices
  if (hasSpecialMinPrice) {
    const nonSpecialMinPrices = cleanMinPrices.filter(
      (price) => Math.abs(price - SPECIAL_MIN_PRICE) >= PRICE_TOLERANCE,
    );
    if (nonSpecialMinPrices.length > 0) {
      lowestValidPrice = Math.min(...nonSpecialMinPrices).toFixed(2);
    } else {
      lowestValidPrice = SPECIAL_MIN_PRICE.toFixed(2);
    }
  } else {
    lowestValidPrice = cleanMinPrices.length > 0 ? Math.min(...cleanMinPrices).toFixed(2) : '';
  }

  // Handle maximum prices
  if (hasSpecialMaxPrice) {
    const nonSpecialMaxPrices = cleanMaxPrices.filter(
      (price) => Math.abs(price - SPECIAL_MAX_PRICE) >= PRICE_TOLERANCE,
    );
    if (nonSpecialMaxPrices.length > 0) {
      highestValidPrice = Math.max(...nonSpecialMaxPrices).toFixed(2);
    } else {
      highestValidPrice = SPECIAL_MAX_PRICE.toFixed(2);
    }
  } else {
    highestValidPrice = cleanMaxPrices.length > 0 ? Math.max(...cleanMaxPrices).toFixed(2) : '';
  }

  return { lowestValidPrice, highestValidPrice };
}
